import React from 'react';
import { useTranslation } from 'react-i18next';
import NavButtons from '../components/NavButtons';
import OpportunityBanner from '../components/OpportunityBanner';
import StepHeading from '../components/StepHeading';
import { BD } from '../bookDemoTheme';
import {
  CHALLENGES,
  HIGH_PRIORITY_ITEMS,
  PLACEHOLDER_SAVINGS_BY_REVENUE,
  UNLOCK_ITEMS,
  resolveFocusChallenges,
} from '../bookDemoConstants';
import { BookDemoFormData } from '../types';

interface ReviewStepProps {
  data: BookDemoFormData;
  onBack: () => void;
  onContinue: () => void;
}

const CARD = (extra = '') => `rounded-xl p-4 sm:p-5 ${BD.cardBg} ${BD.cardShadow} ${extra}`;
const CARD_TITLE = `text-[18px] sm:text-[20px] leading-6 sm:leading-[28px] tracking-[-0.01rem] font-bold ${BD.textPrimary}`;
const FIELD_LABEL = `text-[14px] leading-6 font-normal ${BD.textMuted}`;
const FIELD_VALUE = `text-[16px] leading-6 font-semibold ${BD.textPrimary}`;

const ReviewStep: React.FC<ReviewStepProps> = ({ data, onBack, onContinue }) => {
  const { t } = useTranslation();

  const isMultiLocation = data.restaurantCount20Plus || (data.restaurantCount ?? 1) > 1;
  const locationsValue = data.restaurantCount20Plus
    ? t('bookDemo.locations.valueTwentyPlus')
    : t('bookDemo.locations.value', { count: data.restaurantCount ?? 1 });
  const revenueValue = data.revenueRange ? t(`bookDemo.revenue.options.${data.revenueRange}`) : '—';
  const savings = data.revenueRange ? PLACEHOLDER_SAVINGS_BY_REVENUE[data.revenueRange] : null;

  const challengeTitle = (id: string) => t(`bookDemo.challenges.options.${id}.title`);
  // One line, like the mock's single "biggest challenge" row, but showing the top two.
  const namedChallenges = data.challenges.slice(0, 2).map(challengeTitle).join(' · ');
  const extraChallenges = Math.max(data.challenges.length - 2, 0);
  const challengesValue = extraChallenges
    ? `${namedChallenges} ${t('bookDemo.review.moreChallenges', { count: extraChallenges })}`
    : namedChallenges || '—';

  // Always three tiles, like the mock: their picks first, then the broadest unselected ones.
  const focusItems = resolveFocusChallenges(data.challenges).map(id => ({
    id,
    icon: CHALLENGES.find(c => c.id === id)?.icon,
  }));

  return (
    <div className="flex flex-col flex-1 max-w-[1036px] mx-auto w-full">
      <StepHeading title={t('bookDemo.review.title')} subtitle={t('bookDemo.review.subtitle')} />

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5 mb-5">
        {/* Card A — business snapshot */}
        <div className={CARD()}>
          <h3 className={`${CARD_TITLE} mb-4`}>{t('bookDemo.review.businessTitle')}</h3>

          <div className="grid grid-cols-2 gap-4 sm:gap-0 sm:flex items-stretch">
            <div className="flex-1 min-w-0 sm:pr-4">
              <p className={`${FIELD_LABEL} mb-1`}>{t('bookDemo.review.fields.businessName')}</p>
              <p className={`${FIELD_VALUE} truncate`}>{data.businessName || '—'}</p>
            </div>
            <div className={`w-px self-center hidden sm:flex flex-shrink-0 h-9 ${BD.divider}`} />
            <div className="flex-1 min-w-0 sm:pl-4">
              <p className={`${FIELD_LABEL} mb-1`}>{t('bookDemo.review.fields.locations')}</p>
              <p className={FIELD_VALUE}>{locationsValue}</p>
            </div>
          </div>

          <div className={`h-px w-full my-4 ${BD.divider}`} />

          <div className="flex items-center justify-between gap-3">
            <p className={FIELD_LABEL}>{t('bookDemo.review.fields.challenges')}</p>
            <p className={`${FIELD_VALUE} min-w-0 truncate text-right`}>{challengesValue}</p>
          </div>
          <div className="flex items-center justify-between gap-3 mt-4">
            <p className={FIELD_LABEL}>{t('bookDemo.review.fields.revenue')}</p>
            <p className={`text-[18px] sm:text-[20px] leading-6 font-bold ${BD.accentText}`}>{revenueValue}</p>
          </div>
        </div>

        {/* Card B — we'll focus on */}
        <div className={CARD()}>
          <h3 className={`${CARD_TITLE} mb-5`}>{t('bookDemo.review.focusTitle')}</h3>
          <div className="flex items-start gap-2 sm:gap-0">
            {focusItems.map((item, index) => {
              const Icon = item.icon;
              const isHigh = index < HIGH_PRIORITY_ITEMS;
              return (
                <React.Fragment key={item.id}>
                  {index > 0 && (
                    <div className={`w-px self-center h-[45px] hidden sm:flex flex-shrink-0 mx-4 ${BD.divider}`} />
                  )}
                  <div className="flex-1 min-w-0 flex items-center flex-col text-center">
                    <div
                      className={`w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 ${BD.iconTileBg} ${BD.iconAccent}`}
                    >
                      {Icon && <Icon size={20} strokeWidth={1.5} />}
                    </div>
                    <div className="mt-3 min-w-0">
                      <p className={`text-[16px] leading-5 font-semibold ${BD.textPrimary}`}>
                        {challengeTitle(item.id)}
                      </p>
                      <span
                        className={`inline-block mt-3 text-[10px] leading-5 font-medium px-[7px] py-[1px] rounded-full border ${
                          isHigh ? BD.badgeHigh : BD.badgeMedium
                        }`}
                      >
                        {t(isHigh ? 'bookDemo.review.priority.high' : 'bookDemo.review.priority.medium')}
                      </span>
                    </div>
                  </div>
                </React.Fragment>
              );
            })}
          </div>
        </div>
      </div>

      {/* What we unlock for you — full width */}
      <div className={CARD('sm:pb-8 mb-5')}>
        <h3 className={`${CARD_TITLE} mb-6`}>{t('bookDemo.review.unlockTitle')}</h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:flex lg:items-stretch gap-4 sm:gap-5 lg:gap-0">
          {UNLOCK_ITEMS.map((item, index) => {
            const Icon = item.icon;
            const descriptionKey =
              item.id === 'bestPractice'
                ? `bookDemo.review.unlock.bestPractice.${isMultiLocation ? 'descriptionMulti' : 'descriptionSingle'}`
                : `bookDemo.review.unlock.${item.id}.description`;
            return (
              <React.Fragment key={item.id}>
                {index > 0 && (
                  <div className={`hidden lg:block w-px self-center h-[45px] flex-shrink-0 mx-4 ${BD.divider}`} />
                )}
                <div className="flex-1 min-w-0 flex items-start gap-2">
                  <div
                    className={`w-10 min-w-10 h-10 rounded-lg border flex items-center justify-center ${BD.iconTileSoft} ${BD.iconAccent}`}
                  >
                    <Icon size={20} strokeWidth={1.5} />
                  </div>
                  <div className="min-w-0">
                    <p className={`text-[16px] leading-6 font-semibold mb-1 sm:mb-1.5 ${BD.textPrimary}`}>
                      {t(`bookDemo.review.unlock.${item.id}.title`)}
                    </p>
                    <p className={`text-[14px] leading-5 font-normal ${BD.textMuted}`}>{t(descriptionKey)}</p>
                  </div>
                </div>
              </React.Fragment>
            );
          })}
        </div>
      </div>

      {savings && <OpportunityBanner amount={savings} />}

      <NavButtons onBack={onBack} onContinue={onContinue} />
    </div>
  );
};

export default ReviewStep;
